<script setup>
import { computed, ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Avatar from 'next/avatar/Avatar.vue';
import CardLabels from 'dashboard/components-next/Conversation/ConversationCard/CardLabels.vue';

const props = defineProps({
  contactId: { type: [Number, String], required: true },
  currentConversationId: { type: [Number, String], default: null },
});

const store = useStore();
const route = useRoute();
const router = useRouter();

const conversations = useMapGetter(
  'contactConversations/getAllConversationsByContactId'
);
const stateInbox = useMapGetter('inboxes/getInboxById');
const accountLabels = useMapGetter('labels/getLabels');

const uiFlags = useMapGetter('contactConversations/getUIFlags');
const isFetching = computed(() => uiFlags.value.isFetching);

const contactConversations = computed(() => {
  const list = conversations.value(props.contactId) || [];
  return list
    .slice()
    .sort((a, b) => (b.last_activity_at || 0) - (a.last_activity_at || 0));
});

const showFade = computed(() => contactConversations.value.length > 2);

// Detecta se o usuário rolou até o fim — esconde o fade quando não há mais
// conteúdo abaixo (não faz sentido ter o gradient se já chegou no último).
const listScrollEl = ref(null);
const isAtBottom = ref(false);
const isAtTop = ref(true);

function updateScrollState() {
  const el = listScrollEl.value;
  if (!el) return;
  // Sem scroll real (todos os cards cabem na viewport) → mantém o fade
  // visível como elemento visual indicando "fim da lista". Só esconde
  // quando há scroll de fato e o usuário chega ao final.
  const hasScroll = el.scrollHeight > el.clientHeight + 2;
  if (!hasScroll) {
    isAtBottom.value = false;
    isAtTop.value = true;
    return;
  }
  isAtBottom.value = el.scrollHeight - el.scrollTop - el.clientHeight <= 2;
  isAtTop.value = el.scrollTop <= 2;
}

// O listener é declarado via @scroll no template (mais robusto que
// addEventListener manual — funciona mesmo se o componente é
// re-renderizado por mudança de tab/contato). Aqui só re-checamos quando
// a lista popula (offscreen → scrollable).
onMounted(() => {
  nextTick(updateScrollState);
});

watch(() => contactConversations.value.length, () => {
  nextTick(updateScrollState);
});

// Light/Dark seguindo paleta do Figma. Status pills mantêm o tom verde
// nos casos open/resolved (Figma trata como "neutro positivo"); pending e
// snoozed usam variantes próprias mantendo o alfabeto cromático.
const STATUS_LABELS = {
  open: {
    label: 'Aberta',
    bg: '#DDEDE1',
    color: '#294A31',
    bgDark: '#194121',
    colorDark: '#5AB265',
  },
  pending: {
    label: 'Pendente',
    bg: '#FEF3C7',
    color: '#92400E',
    bgDark: '#584C1E',
    colorDark: '#E3C75B',
  },
  snoozed: {
    label: 'Adiada',
    bg: '#E0E7FF',
    color: '#3730A3',
    bgDark: '#363277',
    colorDark: '#ADA7FB',
  },
  resolved: {
    label: 'Resolvida',
    bg: '#D9EBDE',
    color: '#294A31',
    bgDark: '#194121',
    colorDark: '#5AB265',
  },
};

const formatRelative = ts => {
  if (!ts) return '';
  const date = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
  if (Number.isNaN(date.getTime())) return '';
  return formatDistanceToNow(date, { addSuffix: true, locale: ptBR });
};

const channelType = conv => {
  const inbox = stateInbox.value(conv.inboxId || conv.inbox_id) || {};
  return String(inbox.channel_type || '').toLowerCase();
};

const inboxName = conv => {
  const inbox = stateInbox.value(conv.inboxId || conv.inbox_id) || {};
  return inbox.name || (conv.meta && conv.meta.channel) || 'Canal';
};

const lastMessage = conv => {
  const msgs = (conv && conv.messages) || [];
  for (let i = msgs.length - 1; i >= 0; i--) {
    if (msgs[i].message_type !== 2) return msgs[i];
  }
  return null;
};

const lastMessagePreview = conv => {
  const msg = lastMessage(conv);
  if (!msg) return 'Nenhum conteúdo disponível';
  if (msg.content) return String(msg.content).replace(/\s+/g, ' ').trim();
  if (msg.attachments && msg.attachments.length) {
    const a = msg.attachments[0];
    if (a.file_type === 'image') return '🖼️ Imagem';
    if (a.file_type === 'audio') return '🎤 Áudio';
    if (a.file_type === 'video') return '🎥 Vídeo';
    if (a.file_type === 'file') return '📎 Arquivo';
    return '📎 Anexo';
  }
  return 'Nenhum conteúdo disponível';
};

// Lista de objetos de account labels (necessária pro CardLabels reutilizado).
const accountLabelsList = computed(() => accountLabels.value || []);

const hasLabels = conv => Array.isArray(conv.labels) && conv.labels.length > 0;

const statusInfo = conv => STATUS_LABELS[conv.status] || STATUS_LABELS.open;

const isCurrent = conv =>
  Number(conv.id) === Number(props.currentConversationId);

// SZ: Mapa de rota "list" → "list + conversa" pra preservar contexto e
// evitar remount do ChatList (que reseta chips/filtros) ao trocar de conversa.
// Param names da conversa variam entre rotas (snake_case vs camelCase) — guardamos.
const ROUTE_TO_CONV_ROUTE = {
  home:                            { name: 'inbox_conversation',                    paramKey: 'conversation_id' },
  inbox_dashboard:                 { name: 'conversation_through_inbox',            paramKey: 'conversation_id' },
  label_conversations:             { name: 'conversations_through_label',           paramKey: 'conversation_id' },
  team_conversations:              { name: 'conversations_through_team',            paramKey: 'conversationId'  },
  folder_conversations:            { name: 'conversations_through_folders',         paramKey: 'conversation_id' },
  conversation_mentions:           { name: 'conversation_through_mentions',         paramKey: 'conversationId'  },
  conversation_unattended:         { name: 'conversation_through_unattended',       paramKey: 'conversationId'  },
  conversation_participating:      { name: 'conversation_through_participating',    paramKey: 'conversationId'  },
  // Já em rota "com conversa" — preserva o name, só troca o param
  inbox_conversation:              { name: 'inbox_conversation',                    paramKey: 'conversation_id' },
  conversation_through_inbox:      { name: 'conversation_through_inbox',            paramKey: 'conversation_id' },
  conversations_through_label:     { name: 'conversations_through_label',           paramKey: 'conversation_id' },
  conversations_through_team:      { name: 'conversations_through_team',            paramKey: 'conversationId'  },
  conversations_through_folders:   { name: 'conversations_through_folders',         paramKey: 'conversation_id' },
  conversation_through_mentions:   { name: 'conversation_through_mentions',         paramKey: 'conversationId'  },
  conversation_through_unattended: { name: 'conversation_through_unattended',       paramKey: 'conversationId'  },
  conversation_through_participating: { name: 'conversation_through_participating', paramKey: 'conversationId'  },
};

const conversationPath = conv => {
  const accountId = route.params.accountId;
  const target = conv.display_id || conv.id;
  return `/app/accounts/${accountId}/conversations/${target}`;
};

const openConversation = conv => {
  const target = conv.display_id || conv.id;
  const mapping = ROUTE_TO_CONV_ROUTE[route.name];
  // Sem mapping conhecido → fallback pro path absoluto (comportamento legado)
  if (!mapping) {
    const path = conversationPath(conv);
    router.push(path).catch(() => {
      window.location.href = path;
    });
    return;
  }
  router
    .push({
      name: mapping.name,
      params: { ...route.params, [mapping.paramKey]: target },
    })
    .catch(() => {
      const path = conversationPath(conv);
      window.location.href = path;
    });
};

const openConversationInNewTab = (conv, evt) => {
  if (evt) {
    evt.stopPropagation();
    evt.preventDefault();
  }
  const path = conversationPath(conv);
  window.open(path, '_blank', 'noopener,noreferrer');
};
</script>

<template>
  <div
    v-if="isFetching && !contactConversations.length"
    class="flex items-center justify-center py-10 text-n-slate-11"
  >
    <Spinner />
  </div>
  <div v-else-if="contactConversations.length > 0" class="sz-cph-list-wrap">
    <div
      ref="listScrollEl"
      class="sz-cph-list"
      @scroll.passive="updateScrollState"
    >
      <button
        v-for="conversation in contactConversations"
        :key="conversation.id"
        type="button"
        class="sz-hist-card"
        :class="{ 'sz-hist-card--current': isCurrent(conversation) }"
        @click="openConversation(conversation)"
      >
        <!-- linha 1: status + canal · agente + abrir nova guia -->
        <div class="sz-hist-row sz-hist-row--top">
          <div class="sz-hist-left">
            <span
              class="sz-hist-status"
              :style="{
                '--bg-light': statusInfo(conversation).bg,
                '--bg-dark': statusInfo(conversation).bgDark,
                '--fg-light': statusInfo(conversation).color,
                '--fg-dark': statusInfo(conversation).colorDark,
              }"
            >
              {{ statusInfo(conversation).label }}
            </span>
            <span class="sz-hist-channel">
              <svg
                v-if="channelType(conversation).includes('instagram')"
                width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
              </svg>
              <svg
                v-else-if="channelType(conversation).includes('whatsapp')"
                width="14" height="14" viewBox="0 0 24 24" fill="currentColor"
              >
                <path
                  d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.296-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413"
                />
              </svg>
              <svg
                v-else-if="
                  channelType(conversation).includes('webwidget') ||
                  channelType(conversation).includes('website')
                "
                width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              <svg
                v-else-if="channelType(conversation).includes('email')"
                width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="M3 7l9 6 9-6" />
              </svg>
              <svg
                v-else-if="channelType(conversation).includes('facebook')"
                width="14" height="14" viewBox="0 0 24 24" fill="currentColor"
              >
                <path d="M22 12a10 10 0 10-11.563 9.875V14.89H7.898V12h2.539V9.797c0-2.506 1.493-3.89 3.776-3.89 1.094 0 2.238.196 2.238.196v2.46h-1.26c-1.243 0-1.63.771-1.63 1.563V12h2.773l-.443 2.89h-2.33v6.985A10.002 10.002 0 0022 12z" />
              </svg>
              <svg
                v-else
                width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
              </svg>
            </span>
            <span class="sz-hist-inbox">{{ inboxName(conversation) }}</span>
          </div>
          <div class="sz-hist-right">
            <Avatar
              v-if="conversation.meta && conversation.meta.assignee"
              :src="
                conversation.meta.assignee.thumbnail ||
                conversation.meta.assignee.avatar_url
              "
              :name="conversation.meta.assignee.name"
              :size="16"
              rounded-full
              hide-offline-status
            />
            <span
              v-if="conversation.meta && conversation.meta.assignee"
              class="sz-hist-agent"
            >
              {{ conversation.meta.assignee.name }}
            </span>
            <span
              role="button"
              tabindex="0"
              class="sz-hist-open"
              title="Abrir em outra guia"
              @click="openConversationInNewTab(conversation, $event)"
              @keydown.enter.stop.prevent="openConversationInNewTab(conversation, $event)"
              @keydown.space.stop.prevent="openConversationInNewTab(conversation, $event)"
            >
              <svg
                class="sz-hist-open-icon"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <line x1="7" y1="17" x2="17" y2="7" />
                <polyline points="7 7 17 7 17 17" />
              </svg>
            </span>
          </div>
        </div>

        <!-- linha 2: mensagem + timestamp -->
        <div class="sz-hist-row sz-hist-row--mid">
          <span class="sz-hist-msg">
            &ldquo;{{ lastMessagePreview(conversation) }}&rdquo;
          </span>
          <span class="sz-hist-time">
            {{ formatRelative(conversation.last_activity_at || conversation.created_at) }}
          </span>
        </div>

        <!-- linha 3: labels — reutiliza CardLabels (fade + contador +N + popover) -->
        <div
          v-if="hasLabels(conversation)"
          class="sz-hist-row sz-hist-row--labels"
        >
          <CardLabels
            :conversation-labels="conversation.labels"
            :account-labels="accountLabelsList"
          />
        </div>
      </button>
    </div>
    <!-- Fade TOPO — aparece quando ha scroll e usuario NAO esta no topo -->
    <div
      v-if="showFade"
      class="sz-cph-list-fade sz-cph-list-fade--top"
      :class="{ 'sz-cph-list-fade--hidden': isAtTop }"
      aria-hidden="true"
    ></div>
    <!-- Fade BASE — aparece quando ha scroll e usuario NAO chegou ao fim -->
    <div
      v-if="showFade"
      class="sz-cph-list-fade"
      :class="{ 'sz-cph-list-fade--hidden': isAtBottom }"
      aria-hidden="true"
    ></div>
  </div>
  <div
    v-else
    class="sz-cph-empty"
  >
    <p class="text-sm leading-6 text-center text-n-slate-11">
      Nenhum chat anterior encontrado para este contato.
    </p>
  </div>
</template>

<style>
/* ========================================================================
   ContactPanelHistory — visual conforme Figma (cards 336x72 com 3 linhas).
   Estilos não-scoped pra evitar perda em :global e funcionar em dark.
   ======================================================================== */

/* Empty state preenche 100% do painel (Detalhes/Histórico) */
.sz-cph-empty {
  display: flex;
  flex: 1 1 0%;
  width: 100%;
  height: 100%;
  min-height: 100%;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
}
/* (estilos originais abaixo) */

.sz-cph-list-wrap {
  position: relative;
  --sz-list-bg: #FFFFFF;
}
.dark .sz-cph-list-wrap {
  --sz-list-bg: #17181B;
}

.sz-cph-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
  max-height: 220px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: #D4D4D4 transparent;
}
.dark .sz-cph-list {
  scrollbar-color: #3E3E43 transparent;
}
.sz-cph-list::-webkit-scrollbar { width: 4px; }
.sz-cph-list::-webkit-scrollbar-track { background: transparent; }
.sz-cph-list::-webkit-scrollbar-thumb {
  background: #D4D4D4;
  border-radius: 4px;
}
.dark .sz-cph-list::-webkit-scrollbar-thumb { background: #3E3E43; }

/* Fade gradient ≈ 1.5 cards. Curva 0%→50% pra a opacidade subir mais
   rápido — assim o efeito visual cobre desde o topo do 3º card (no
   Figma a "Resolvida" e o avatar do 3º item já aparecem desbotados). */
.sz-cph-list-fade {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 40px;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    var(--sz-list-bg) 60%,
    var(--sz-list-bg) 100%
  );
  z-index: 4;
  opacity: 1;
  transition: opacity 0.18s ease;
}
/* Variante TOPO — ancora em cima e inverte direcao do gradient */
.sz-cph-list-fade--top {
  top: 0;
  bottom: auto;
  background: linear-gradient(
    to top,
    transparent 0%,
    var(--sz-list-bg) 60%,
    var(--sz-list-bg) 100%
  );
}
.sz-cph-list-fade--hidden { opacity: 0; }

/* ----- card ----- */
.sz-hist-card {
  --sz-card-bg: #FFFFFF;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 8px;
  border: 1px solid #EFEFEF;
  border-radius: 8px;
  background: var(--sz-card-bg);
  cursor: pointer;
  text-align: left;
  transition: background-color 0.12s ease, border-color 0.12s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}
.sz-hist-card:hover {
  --sz-card-bg: #F7FAF8;
}

.sz-hist-card--current {
  --sz-card-bg: #EEF6F0;
  border-color: transparent;
}
.sz-hist-card--current:hover {
  --sz-card-bg: #E5F1E9;
}

/* dark mode — card normal #17191C (cor padrão do projeto), current #26282B */
.dark .sz-hist-card {
  --sz-card-bg: #17191C;
  border-color: #252629;
}
.dark .sz-hist-card:hover {
  --sz-card-bg: #1C1E22;
}
.dark .sz-hist-card--current {
  --sz-card-bg: #26282B;
  border-color: transparent;
}
.dark .sz-hist-card--current:hover {
  --sz-card-bg: #2C2E32;
}

/* ----- linhas ----- */
.sz-hist-row {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}
.sz-hist-row--top {
  justify-content: space-between;
  height: 20px;
}
.sz-hist-row--mid {
  gap: 12px;
  height: 20px;
}
.sz-hist-row--labels {
  flex-wrap: wrap;
  gap: 4px;
  padding: 2px 0 0;
}

/* ----- linha 1: esquerda ----- */
.sz-hist-left {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  flex: 1 1 auto;
}
.sz-hist-status {
  display: inline-flex;
  align-items: center;
  height: 14px;
  padding: 0 6px;
  border-radius: 29px;
  font-size: 10px;
  font-weight: 500;
  line-height: 1;
  background: var(--bg-light);
  color: var(--fg-light);
  white-space: nowrap;
  flex-shrink: 0;
}
.dark .sz-hist-status {
  background: var(--bg-dark);
  color: var(--fg-dark);
}
.sz-hist-channel {
  display: inline-flex;
  align-items: center;
  width: 16px;
  height: 16px;
  color: #000000;
  flex-shrink: 0;
}
.dark .sz-hist-channel { color: #FFFFFF; }
.sz-hist-inbox {
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  color: #000000;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dark .sz-hist-inbox { color: #FFFFFF; }

/* ----- linha 1: direita (avatar + agente + abrir) ----- */
.sz-hist-right {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  min-width: 0;
  max-width: 50%;
}
.sz-hist-agent {
  font-size: 10px;
  font-weight: 400;
  line-height: 20px;
  color: #525252;
  max-width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.dark .sz-hist-agent { color: #9797A0; }
.sz-hist-open {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-left: 2px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.12s ease;
  flex-shrink: 0;
  padding: 0;
}
.sz-hist-open:hover { background: rgba(0, 0, 0, 0.06); }
.dark .sz-hist-open:hover { background: rgba(255, 255, 255, 0.10); }

/* Ícone arrow-up-right (Lucide). Aplica stroke direto em line/polyline pra
   não depender de currentColor — alguns resets globais zeram stroke em
   SVGs filhos de elementos interativos. !important garante prioridade. */
.sz-hist-open-icon {
  display: block;
  width: 14px;
  height: 14px;
  pointer-events: none;
  fill: none !important;
}
.sz-hist-open-icon line,
.sz-hist-open-icon polyline {
  stroke: #4B5563 !important;
  fill: none !important;
}
.sz-hist-open:hover .sz-hist-open-icon line,
.sz-hist-open:hover .sz-hist-open-icon polyline {
  stroke: #111827 !important;
}
.dark .sz-hist-open-icon line,
.dark .sz-hist-open-icon polyline {
  stroke: #9797A0 !important;
}
.dark .sz-hist-open:hover .sz-hist-open-icon line,
.dark .sz-hist-open:hover .sz-hist-open-icon polyline {
  stroke: #F3F4F6 !important;
}

/* ----- linha 2 ----- */
.sz-hist-msg {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  color: #423939;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dark .sz-hist-msg { color: #9797A0; }
.sz-hist-time {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 400;
  line-height: 20px;
  color: #000000;
  white-space: nowrap;
}
.dark .sz-hist-time { color: #8A8A93; }

/* ----- linha 3: labels ----- */
/* Os pills, fade e contador são renderizados pelo CardLabels reutilizado.
   min-height (não height fixo) — quando o user clica no +N, o CardLabels
   vira flex-wrap e cresce verticalmente; o row precisa acompanhar pra o
   card pai crescer e empurrar os irmãos pra baixo. */
.sz-hist-row--labels {
  min-height: 22px;
  height: auto;
  padding-top: 2px;
  align-items: flex-start;
}
</style>
